import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { ShopSiteSetting } from '@/lib/api/shop-site-settings-api'
import { SettingFields } from '@/components/retail/cms/site-setting-fields'
import {
    SITE_SETTING_FIELDS,
    formPath,
    settingSchema,
    toData,
    toForm,
} from '@/components/retail/cms/site-settings-model'
import { ErrorText } from '@/components/retail/form-bits'
import { shopFieldErrors } from '@/components/retail/shop-errors'
import { Button } from '@/components/ui/button'
import { useSaveShopSiteSetting } from '@/hooks/use-shop-site-settings-query'
import { ApiError } from '@/lib/api/client'
import { m } from '@/lib/paraglide/messages'

type FormValues = Record<string, unknown>

/** Form một kind cài đặt website; lưu cả kind (PUT, có version). */
export function SiteSettingForm({
    setting,
    canWrite,
    onReload,
}: {
    setting: ShopSiteSetting
    canWrite: boolean
    onReload: () => void
}) {
    const fields = SITE_SETTING_FIELDS[setting.kind]
    const save = useSaveShopSiteSetting()
    const [conflict, setConflict] = useState(false)
    const form = useForm<FormValues>({
        resolver: zodResolver(settingSchema(fields)),
        defaultValues: toForm(fields, setting.data),
    })

    const submit = form.handleSubmit(async (values) => {
        setConflict(false)
        try {
            await save.mutateAsync({
                kind: setting.kind,
                input: {
                    version: setting.version,
                    data: toData(fields, values),
                },
            })
        } catch (err) {
            for (const [field, message] of Object.entries(
                shopFieldErrors(err, m.siteSettings_save_failed()),
            )) {
                form.setError(
                    field === 'form' ? 'root.server' : formPath(fields, field),
                    {
                        type: 'server',
                        message,
                    },
                )
            }
            setConflict(
                err instanceof ApiError &&
                    err.code === 'SHOP_CONCURRENT_CHANGE',
            )
        }
    })

    return (
        <FormProvider {...form}>
            <form onSubmit={submit} noValidate className="space-y-4">
                <fieldset disabled={!canWrite} className="space-y-4">
                    <SettingFields fields={fields} />
                </fieldset>
                <ErrorText text={form.formState.errors.root?.server?.message} />
                {conflict && (
                    <Button type="button" variant="outline" onClick={onReload}>
                        {m.siteSettings_reload()}
                    </Button>
                )}
                {canWrite && (
                    <Button type="submit" disabled={save.isPending}>
                        {save.isPending ? m.common_saving() : m.common_save()}
                    </Button>
                )}
            </form>
        </FormProvider>
    )
}
